import dayjs from "dayjs";
import { ReactNode } from "react";
import styled from "styled-components";

import {
  CERTIFICATE_COLORS as C,
  CERTIFICATE_LOGO_SVG,
  CERTIFICATE_PALETTE,
  CERTIFICATE_PALETTE_MUTED,
  CERTIFICATE_TITLE,
  CREDENTIAL_LABEL,
  cssGradient,
  curriculumVersionText,
  DATE_LABEL,
  ICertificatePalette,
  SEAL_GEOMETRY,
  SEAL_IMAGE_SRC,
  SEAL_VIEW_BOX,
} from "./certificateDesign";

/**
 * CLE-3015 — the Clear Certified certificate on screen, shared by the signed-in certificate page
 * (/training/certificate, which also prints it) and the public verification page
 * (/certified/:credentialId). The PDF (pages/CertificatePdf.tsx) draws the same design from the same
 * constants (certificateDesign.ts).
 */

/** "October 21, 2026", in the viewer's local time. */
export const formatCertificateDate = (iso: string) =>
  dayjs(iso).format("MMMM D, YYYY");

const PHONE = "600px";

/** The outer band (the logo's teal → brand purple → navy) around a white sheet. Landscape on a wide screen. */
const Frame = styled.section<{ $palette: ICertificatePalette }>`
  box-sizing: border-box;
  width: 100%;
  max-width: 920px;
  padding: 14px;
  background: ${({ $palette }) => cssGradient("135deg", $palette.band)};
  box-shadow: 0 12px 32px rgba(23, 20, 63, 0.16);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  @media (max-width: ${PHONE}) {
    padding: 8px;
  }
`;

const Sheet = styled.div`
  box-sizing: border-box;
  height: 100%;
  padding: 8px;
  background: ${C.white};
`;

/** The inner hairline, and the content laid out top to bottom. */
const Inner = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  height: 100%;
  min-height: 560px;
  padding: 28px 48px 16px;
  border: 1px solid ${C.grayLine};
  color: ${C.navy};
  text-align: center;

  @media (max-width: ${PHONE}) {
    min-height: 0;
    padding: 20px 14px 12px;
  }
`;

const Logo = styled.img`
  display: block;
  height: 40px;
  margin-bottom: 14px;

  @media (max-width: ${PHONE}) {
    height: 30px;
  }
`;

const Eyebrow = styled.div`
  color: ${C.gray};
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;

  @media (max-width: ${PHONE}) {
    font-size: 0.68rem;
    letter-spacing: 0.16em;
  }
`;

const Title = styled.h1<{ $color: string }>`
  margin: 4px 0 18px;
  color: ${({ $color }) => $color};
  font-size: clamp(2rem, 6vw, 3rem);
  font-weight: 800;
  line-height: 1.1;
`;

const Certifies = styled.div`
  color: ${C.gray};
  font-size: 1rem;
  font-style: italic;
`;

const Name = styled.div`
  max-width: 100%;
  margin: 6px 0 0;
  overflow-wrap: anywhere;
  color: ${C.navy};
  font-family: Georgia, "Times New Roman", Times, serif;
  font-size: clamp(2rem, 7vw, 3.1rem);
  font-weight: 700;
  line-height: 1.15;
`;

/** The short gradient rule under the name. */
const Rule = styled.div<{ $palette: ICertificatePalette }>`
  width: min(320px, 70%);
  height: 3px;
  margin: 12px 0 14px;
  border-radius: 2px;
  background: ${({ $palette }) => cssGradient("90deg", $palette.rule)};
`;

const Hospital = styled.div`
  margin-bottom: 8px;
  color: ${C.navy};
  font-size: 1.2rem;
  font-weight: 600;
`;

const Body = styled.p`
  max-width: 520px;
  margin: 0 0 8px;
  color: ${C.gray};
  font-size: 1.05rem;
  line-height: 1.45;
`;

/** Date, seal, credential id: a row on a wide screen, a column (seal first) on a phone. */
const Footer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: end;
  gap: 24px;
  width: 100%;
  margin-top: 16px;

  @media (max-width: ${PHONE}) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 12px;

    > [data-seal] {
      order: -1;
    }
  }
`;

const Field = styled.div`
  justify-self: center;
  width: 100%;
  max-width: 220px;

  .value {
    padding-bottom: 6px;
    border-bottom: 1px solid ${C.grayLight};
    color: ${C.navy};
    font-size: 1.05rem;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .value.mono {
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 0.95rem;
    letter-spacing: 0.08em;
  }

  .label {
    margin-top: 6px;
    color: ${C.gray};
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }
`;

const Version = styled.div`
  margin-top: 10px;
  color: ${C.gray};
  font-size: 0.7rem;
`;

const SealBox = styled.div`
  display: flex;
  justify-content: center;
  width: 96px;
  height: 120px;

  svg,
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

interface ICertificateSealProps {
  /** Grays, for a credential that is not current. */
  muted?: boolean;
  /** Supplied artwork instead of the drawn seal; defaults to SEAL_IMAGE_SRC (null: draw it). */
  imageSrc?: string | null;
}

/**
 * The "Clear Certified" seal. Drawn in SVG from SEAL_GEOMETRY (the PDF draws the same shapes); when
 * supplied artwork exists, SEAL_IMAGE_SRC (certificateDesign.ts) swaps it in here and in the PDF.
 */
export function CertificateSeal({
  muted = false,
  imageSrc = SEAL_IMAGE_SRC,
}: ICertificateSealProps) {
  if (imageSrc) {
    return (
      <SealBox data-seal="image">
        <img src={imageSrc} alt="Clear Certified seal" />
      </SealBox>
    );
  }
  const palette = muted ? CERTIFICATE_PALETTE_MUTED : CERTIFICATE_PALETTE;
  const { seal } = palette;
  // Both seals on a page share the same definition, so a fixed id per palette is safe.
  const gradientId = `clear-certified-seal-${muted ? "muted" : "brand"}`;
  const g = SEAL_GEOMETRY;
  return (
    <SealBox data-seal="drawn">
      <svg
        viewBox={`0 0 ${SEAL_VIEW_BOX.width} ${SEAL_VIEW_BOX.height}`}
        role="img"
        aria-label="Clear Certified seal"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            {seal.rosette.map(({ offset, color }) => (
              <stop key={offset} offset={offset} stopColor={color} />
            ))}
          </linearGradient>
        </defs>
        <polygon points={g.tails[0]} fill={seal.tails[0]} />
        <polygon points={g.tails[1]} fill={seal.tails[1]} />
        <polygon points={g.rosette} fill={`url(#${gradientId})`} />
        <circle
          cx={g.center.x}
          cy={g.center.y}
          r={g.ringRadius}
          fill={seal.ring}
        />
        <circle
          cx={g.center.x}
          cy={g.center.y}
          r={g.discRadius}
          fill={seal.disc}
        />
        <circle
          cx={g.center.x}
          cy={g.center.y}
          r={g.hairlineRadius}
          fill="none"
          stroke={seal.hairline}
          strokeWidth={1}
        />
        <path
          d={g.check}
          fill="none"
          stroke={seal.check}
          strokeWidth={g.checkWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </SealBox>
  );
}

export interface ICertificateCardProps {
  name: string;
  hospitalName?: string | null;
  /** Formatted ("October 21, 2026"); null leaves the date blank (a credential that is not current). */
  date?: string | null;
  credentialId?: string | null;
  curriculumVersion?: number | null;
  eyebrow: string;
  certifies: string;
  /** The sentence under the name; null leaves it out. */
  body?: string | null;
  /** Grays, for a credential that is not current. */
  muted?: boolean;
  /** "certificate" on the signed-in page, "credential" on the public one: the data-testid prefix. */
  testIdPrefix: string;
  className?: string;
  ariaLabel: string;
  /** Extra content between the body and the footer (the public page's status). */
  children?: ReactNode;
}

/** The whole certificate, as both pages show it. */
export function CertificateCard({
  name,
  hospitalName,
  date,
  credentialId,
  curriculumVersion,
  eyebrow,
  certifies,
  body,
  muted = false,
  testIdPrefix,
  className,
  ariaLabel,
  children,
}: ICertificateCardProps) {
  const palette = muted ? CERTIFICATE_PALETTE_MUTED : CERTIFICATE_PALETTE;
  return (
    <Frame
      $palette={palette}
      className={className}
      aria-label={ariaLabel}
      data-muted={muted ? "true" : undefined}
    >
      <Sheet>
        <Inner>
          <Logo src={CERTIFICATE_LOGO_SVG} alt="Clear Health" />
          <Eyebrow>{eyebrow}</Eyebrow>
          <Title $color={palette.title}>{CERTIFICATE_TITLE}</Title>
          <Certifies>{certifies}</Certifies>
          <Name data-testid={`${testIdPrefix}-name`}>{name}</Name>
          <Rule $palette={palette} />
          {hospitalName ? (
            <Hospital data-testid={`${testIdPrefix}-hospital`}>
              {hospitalName}
            </Hospital>
          ) : null}
          {body ? <Body>{body}</Body> : null}
          {children}
          <Footer>
            <Field>
              {date ? (
                <>
                  <div className="value" data-testid={`${testIdPrefix}-date`}>
                    {date}
                  </div>
                  <div className="label">{DATE_LABEL}</div>
                </>
              ) : null}
            </Field>
            <CertificateSeal muted={muted} />
            <Field>
              {credentialId ? (
                <>
                  <div
                    className="value mono"
                    data-testid={`${testIdPrefix}-credential-id`}
                  >
                    {credentialId}
                  </div>
                  <div className="label">{CREDENTIAL_LABEL}</div>
                </>
              ) : null}
            </Field>
          </Footer>
          {curriculumVersion ? (
            <Version>{curriculumVersionText(curriculumVersion)}</Version>
          ) : null}
        </Inner>
      </Sheet>
    </Frame>
  );
}
